import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";

export type ProductionFileLoadingStatus =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "REVISION_REQUESTED"
  | "DELAYED";

export type ProductionFilePriority = "CAT_01" | "CAT_02" | "CAT_03";

export interface ProductionFileEntryInput {
  fileType: string;
  revision?: string;
  notes?: string;
}

export interface ProductionFileLoadingInput {
  id?: number;
  fileName: string;
  projectNumber: string;
  wmsplProjectNumber?: string;
  projectWbs?: string;
  onepwsPoNumber?: string;
  reservationNumbers?: string;
  productionUnit?: string;
  woodSubCategory?: string;
  numberOfComponents?: number;
  engineerName?: string;
  engineerId?: number;
  requestorName?: string;
  requestorEmail?: string;
  department?: string;
  jobWorkDepartments?: string | string[];
  jobWorkStages?: any[];
  currentJobWorkStage?: string;
  storeMailRecipients?: string;
  fileLoadingRequestDate?: string;
  requiredLoadingDate?: string;
  actualLoadingDate?: string;
  loadingStatus?: ProductionFileLoadingStatus;
  priorityLevel?: ProductionFilePriority;
  portalStage?: string;
  remarks?: string;
  replaceFiles?: boolean;
  skipEmail?: boolean;
  sendFilesManually?: boolean;
  fileEntries: ProductionFileEntryInput[];
}

export interface ProductionFileLoadingRecord extends ProductionFileLoadingInput {
  id: number;
  daysPending: number;
  delayAgainstRequiredDate: number;
  lastActivityAt?: string;
  lastActivityByName?: string;
  attachments?: any[];
  activityLogs?: any[];
  communicationLogs?: any[];
  portalStage?: string;
  portalStatus?: string;
}

export interface ProductionFileLoadingSearchResponse {
  content: ProductionFileLoadingRecord[];
  hasNext: boolean;
  hasPrevious: boolean;
  numberOfElements: number;
  number: number;
}

export interface ProductionFileLoadingDashboard {
  totalPendingLoads: number;
  completedLoads: number;
  delayedLoads: number;
  totalProjects: number;
}

export interface ProductionFileStageImportRow {
  poNumber: string;
  stage: string;
  wmsplProjectNumber: string;
}

export interface ProductionFileStageImportSummary {
  updatedCount: number;
  skippedCount: number;
  notFoundCount: number;
  failedCount: number;
  details: Array<{
    poNumber: string;
    stage: string;
    wmsplProjectNumber: string;
    status: "UPDATED" | "SKIPPED" | "NOT_FOUND" | "FAILED";
    message?: string;
  }>;
}

const appendPayload = (payload: ProductionFileLoadingInput, files: File[] = []) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === "fileEntries" || value === undefined || value === null || value === "") {
      return;
    }
    formData.append(key, String(value));
  });
  payload.fileEntries?.forEach((entry, index) => {
    Object.entries(entry).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(`fileEntries[${index}].${key}`, String(value));
      }
    });
  });
  files.forEach((file) => formData.append("files", file));
  return formData;
};

export interface ProductionFileFetchJobLog {
  id: number;
  jobMode: "MANUAL" | "AUTO";
  status: "PENDING" | "RUNNING" | "FAILED" | "SUCCESS";
  scheduledFor?: string;
  startedAt?: string;
  completedAt?: string;
  portalProjects: number;
  productionRecords: number;
  matchedRecords: number;
  updatedRecords: number;
  skippedRecords: number;
  failedRecords: number;
  errorMessage?: string;
  createdAt?: string;
  lastAttemptAt?: string;
}
export const ProductionFileLoadingApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "productionFileLoadingApi",
  tagTypes: ["ProductionFileLoading"],
  endpoints: (build) => ({
    getProductionFileLoadingDashboard: build.query<ProductionFileLoadingDashboard, void>({
      query: () => ({
        url: "production-file-loading-status/dashboard",
        method: "GET",
      }),
      providesTags: ["ProductionFileLoading"],
    }),
    getProductionFileLoading: build.query<ProductionFileLoadingSearchResponse, any>({
      query: (params) => ({
        url: "production-file-loading-status",
        method: "GET",
        params,
      }),
      providesTags: ["ProductionFileLoading"],
    }),
    createProductionFileLoading: build.mutation<any, { payload: ProductionFileLoadingInput; files: File[] }>({
      query: ({ payload, files }) => ({
        url: "production-file-loading-status",
        method: "POST",
        data: appendPayload(payload, files),
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    updateProductionFileLoading: build.mutation<any, { id: number; payload: ProductionFileLoadingInput; files: File[] }>({
      query: ({ id, payload, files }) => ({
        url: `production-file-loading-status/${id}`,
        method: "PUT",
        data: appendPayload(payload, files),
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    requestProductionFileRevision: build.mutation<any, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `production-file-loading-status/${id}/revision`,
        method: "POST",
        params: { reason },
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    triggerProductionFileFollowUp: build.mutation<any, { id: number; recipients?: string; message?: string }>({
      query: ({ id, recipients, message }) => ({
        url: `production-file-loading-status/${id}/follow-up`,
        method: "POST",
        params: { recipients, message },
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    importProductionFileStages: build.mutation<ProductionFileStageImportSummary, ProductionFileStageImportRow[]>({
      query: (rows) => ({
        url: "production-file-loading-status/import",
        method: "POST",
        data: rows,
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    applyFetchedProductionProjects: build.mutation<any, { rows: any[] }>({
      query: ({ rows }) => ({
        url: "production-file-loading-status/fetch-projects/apply",
        method: "POST",
        data: rows,
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
    getProductionFileFetchLogs: build.query<ProductionFileFetchJobLog[], number | void>({
      query: (limit = 50) => ({
        url: "production-file-loading-status/fetch-projects/logs",
        method: "GET",
        params: { limit },
      }),
      providesTags: ["ProductionFileLoading"],
    }),    deleteProductionFileLoading: build.mutation<any, number>({
      query: (id) => ({
        url: `production-file-loading-status/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProductionFileLoading"],
    }),
  }),
});

export const {
  useGetProductionFileLoadingDashboardQuery,
  useGetProductionFileLoadingQuery,
  useLazyGetProductionFileLoadingQuery,
  useCreateProductionFileLoadingMutation,
  useUpdateProductionFileLoadingMutation,
  useRequestProductionFileRevisionMutation,
  useTriggerProductionFileFollowUpMutation,
  useImportProductionFileStagesMutation,
  useApplyFetchedProductionProjectsMutation,
  useGetProductionFileFetchLogsQuery,
  useDeleteProductionFileLoadingMutation,
} = ProductionFileLoadingApi;












